import React from 'react';
import { Beaker, Droplets, Package } from 'lucide-react';
import { Heading } from '../layout';
import { TabNavigation } from '../reusable';
import { useUIColors } from '../../hooks/useTheme';
import ProductionDataTable from './ProductionDataTable';
import ProductionStatusChip from './ProductionStatusChip';
import useProductionStore from './useProductionStore';
import {
  getBatchStatusColor,
  formatDateEs,
  formatQty,
  FORMULA_KIND_COLOR,
} from './productionDomain';
import { inferProcessStage, REQUEST_STATUS } from './planningDomain';

const cell = (children, align = 'left') => (
  <td style={{ padding: '8px 10px', fontSize: 13, textAlign: align }}>{children}</td>
);

const actionBtn = (label, onClick, danger = false) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      height: 28,
      padding: '0 10px',
      borderRadius: 6,
      border: 'none',
      background: danger ? '#dc2626' : '#16a34a',
      color: '#fff',
      fontSize: 11,
      fontWeight: 700,
      cursor: 'pointer',
    }}
  >
    {label}
  </button>
);

const PaintShopProductionWorkspace = ({ isDarkMode = false }) => {
  const ui = useUIColors();
  const { batches, requests, persistBatches, updateProductRequestPlanning } = useProductionStore();

  const advance = (batch, patch, requestStatus) => {
    persistBatches((current) =>
      current.map((row) =>
        row.id === batch.id
          ? {
              ...row,
              ...patch,
              events: [
                ...(row.events || []),
                {
                  status: patch.status || row.status,
                  at: new Date().toISOString().slice(0, 10),
                  note: patch.notes_event || '',
                },
              ],
            }
          : row
      )
    );
    if (requestStatus && batch.request_code) {
      const request = requests.find((row) => row.code === batch.request_code);
      if (request?.request_id) {
        updateProductRequestPlanning(request.request_id, {
          status: requestStatus,
          op_code: batch.code,
        }).catch((error) => console.error('No se pudo actualizar la solicitud:', error));
      }
    }
  };

  const byStage = (stage) => batches.filter((row) => inferProcessStage(row) === stage);

  const fabricacion = byStage('fabricacion');
  const envasado = byStage('envasado');
  const tintometria = byStage('tintometria');

  const columns = [
    { key: 'Nº OP', sortKey: 'code' },
    { key: 'Producto', sortKey: 'product_name' },
    { key: 'Fórmula', sortKey: 'formula_code' },
    { key: 'Cantidad', sortKey: 'planned_qty', align: 'right' },
    { key: 'Turno', sortKey: 'responsible' },
    { key: 'Fecha', sortKey: 'planned_date' },
    { key: 'Estado', sortKey: 'status', align: 'center' },
    { key: '', sortKey: null, align: 'right' },
  ];

  const renderCommon = (row, actions) => (
    <>
      {cell(<strong>{row.code}</strong>)}
      {cell(`${row.product_code} · ${row.product_name}`)}
      {cell(row.formula_code || '—')}
      {cell(formatQty(row.actual_qty || row.planned_qty, row.unit), 'right')}
      {cell(row.responsible)}
      {cell(formatDateEs(row.planned_date))}
      {cell(<ProductionStatusChip status={row.status} color={getBatchStatusColor(row.status)} />, 'center')}
      {cell(actions, 'right')}
    </>
  );

  const tabs = [
    {
      key: 'fabricacion',
      label: 'Fabricación',
      icon: <Beaker size={16} />,
      content: (
        <ProductionDataTable
          title="Fabricación"
          subtitle={`${fabricacion.length} órdenes en molino / mezclado`}
          rows={fabricacion}
          columns={columns}
          defaultSortKey="planned_date"
          searchPlaceholder="Buscar OP, producto, turno…"
          renderCells={(row) =>
            renderCommon(
              row,
              row.status === 'Terminado' || row.status === 'Anulada' ? (
                '—'
              ) : row.formula_kind === FORMULA_KIND_COLOR ||
                String(row.formula_code || '').startsWith('FO-C-') ||
                ['A006', 'A012'].includes(row.product_code) ? (
                actionBtn('Enviar a tintometría', () =>
                  advance(
                    row,
                    {
                      status: 'En calidad',
                      process_stage: 'tintometria',
                      actual_qty: row.actual_qty || row.planned_qty,
                      notes_event: 'Base fabricada. Pasa a tintometría.',
                    },
                    REQUEST_STATUS.inProcess
                  )
                )
              ) : (
                actionBtn('Enviar a envasado', () =>
                  advance(
                    row,
                    {
                      status: 'Fabricado',
                      process_stage: 'envasado',
                      actual_qty: row.actual_qty || row.planned_qty,
                      notes_event: 'Fabricación cerrada. Pasa a envasado.',
                    },
                    REQUEST_STATUS.inProcess
                  )
                )
              )
            )
          }
        />
      ),
    },
    {
      key: 'envasado',
      label: 'Envasado',
      icon: <Package size={16} />,
      content: (
        <ProductionDataTable
          title="Envasado"
          subtitle={`${envasado.length} órdenes en llenado`}
          rows={envasado}
          columns={[
            { key: 'Nº OP', sortKey: 'code' },
            { key: 'Producto', sortKey: 'product_name' },
            { key: 'Formato', sortKey: 'format' },
            { key: 'Cantidad', sortKey: 'planned_qty', align: 'right' },
            { key: 'Turno', sortKey: 'responsible' },
            { key: 'Fecha', sortKey: 'planned_date' },
            { key: 'Estado', sortKey: 'status', align: 'center' },
            { key: '', sortKey: null, align: 'right' },
          ]}
          defaultSortKey="planned_date"
          searchPlaceholder="Buscar OP, formato, producto…"
          renderCells={(row) => (
            <>
              {cell(<strong>{row.code}</strong>)}
              {cell(`${row.product_code} · ${row.product_name}`)}
              {cell(row.format || 'Tineta 16 L')}
              {cell(formatQty(row.actual_qty || row.planned_qty, row.unit), 'right')}
              {cell(row.responsible)}
              {cell(formatDateEs(row.planned_date))}
              {cell(
                <ProductionStatusChip status={row.status} color={getBatchStatusColor(row.status)} />,
                'center'
              )}
              {cell(
                row.status === 'Terminado' ? (
                  'Cerrada'
                ) : (
                  actionBtn('Cerrar OP (PT)', () =>
                    advance(
                      row,
                      {
                        status: 'Terminado',
                        process_stage: 'envasado',
                        notes_event: 'Envasado conforme. Liberado a producto terminado.',
                      },
                      REQUEST_STATUS.finished
                    )
                  )
                ),
                'right'
              )}
            </>
          )}
        />
      ),
    },
    {
      key: 'tintometria',
      label: 'Tintometría',
      icon: <Droplets size={16} />,
      content: (
        <ProductionDataTable
          title="Tintometría"
          subtitle={`${tintometria.length} órdenes en ajuste de color`}
          rows={tintometria}
          columns={[
            { key: 'Nº OP', sortKey: 'code' },
            { key: 'Producto', sortKey: 'product_name' },
            { key: 'Color', sortKey: 'color_code' },
            { key: 'Fórmula', sortKey: 'formula_code' },
            { key: 'Cantidad', sortKey: 'planned_qty', align: 'right' },
            { key: 'Turno', sortKey: 'responsible' },
            { key: 'Estado', sortKey: 'status', align: 'center' },
            { key: '', sortKey: null, align: 'right' },
          ]}
          defaultSortKey="planned_date"
          searchPlaceholder="Buscar OP, RAL, color…"
          renderCells={(row) => (
            <>
              {cell(<strong>{row.code}</strong>)}
              {cell(`${row.product_code} · ${row.product_name}`)}
              {cell(row.color_code || '—')}
              {cell(row.formula_code || '—')}
              {cell(formatQty(row.actual_qty || row.planned_qty, row.unit), 'right')}
              {cell(row.responsible)}
              {cell(
                <ProductionStatusChip status={row.status} color={getBatchStatusColor(row.status)} />,
                'center'
              )}
              {cell(
                <span style={{ display: 'inline-flex', gap: 6 }}>
                  {actionBtn('Color OK → envasado', () =>
                    advance(
                      row,
                      {
                        status: 'Fabricado',
                        process_stage: 'envasado',
                        notes_event: 'Tintometría conforme. Pasa a envasado.',
                      },
                      REQUEST_STATUS.inProcess
                    )
                  )}
                  {actionBtn(
                    'Reproceso',
                    () =>
                      advance(
                        row,
                        {
                          status: 'Reproceso',
                          process_stage: 'fabricacion',
                          notes_event: 'Color fuera de rango. Devuelto a fabricación.',
                        },
                        REQUEST_STATUS.inProcess
                      ),
                    true
                  )}
                </span>,
                'right'
              )}
            </>
          )}
        />
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Heading level={2} isDarkMode={isDarkMode} style={{ marginBottom: 6 }}>
          Producción
        </Heading>
        <p style={{ margin: 0, maxWidth: 720, fontSize: 13, color: ui.muted, lineHeight: 1.45 }}>
          Ejecución en planta: fabricar la base o el lote, ajustar el color en tintometría y envasar a
          producto terminado.
        </p>
      </div>
      <TabNavigation tabs={tabs} defaultTab="fabricacion" isDarkMode={isDarkMode} />
    </div>
  );
};

export default PaintShopProductionWorkspace;
